import React, { useState, useRef, useCallback, useEffect } from 'react';

export interface DropdownItem {
  label: string;
  href?: string;
  onClick?: () => void;
  items?: DropdownItem[];
  count?: number;
}

interface DropdownProps {
  title: string;
  items: DropdownItem[];
  className?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({ title, items, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoveredSubIndex, setHoveredSubIndex] = useState<{parent: number; child: number} | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setHoveredIndex(null);
      setHoveredSubIndex(null);
    }, 150);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleItemClick = (item: DropdownItem) => {
    if (!item.items && item.onClick) {
      item.onClick();
      setIsOpen(false);
    }
  };

  return (
    <div 
      className={`relative ${className}`} 
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 focus:outline-none"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {title}
        <svg
          className={`ml-1 h-4 w-4 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 z-50 mt-1 w-auto min-w-[160px] origin-top bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600 focus:outline-none transform transition-all duration-200 ease-out">
          <div className="py-1">
            {items.map((item, index) => (
              <div
                key={index}
                className="relative"
                onMouseEnter={() => item.items && setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <button
                  className={`block w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-150 whitespace-nowrap ${
                    item.items ? 'flex items-center justify-between' : ''
                  }`}
                  onClick={() => handleItemClick(item)}
                >
                  <span className="flex items-center gap-2">
                    {item.label}
                    {item.count !== undefined && (
                      <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full min-w-[1.25rem]">
                        {item.count.toLocaleString()}
                      </span>
                    )}
                  </span>
                  {item.items && (
                    <svg
                      className="ml-2 h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
                {item.items && hoveredIndex === index && (
                  <div className="absolute left-full top-0 -ml-1 pl-2 z-50">
                    <div className="w-auto min-w-[140px] bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600 py-1">
                      {item.items.map((subItem, subIndex) => (
                        <div 
                          key={subIndex} 
                          className="relative"
                          onMouseEnter={() => subItem.items && setHoveredSubIndex({parent: index, child: subIndex})}
                          onMouseLeave={() => setHoveredSubIndex(null)}
                        >
                          <button
                            className={`block w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-150 whitespace-nowrap ${
                              subItem.items ? 'flex items-center justify-between' : ''
                            }`}
                            onClick={() => {
                              if (!subItem.items && subItem.onClick) {
                                subItem.onClick();
                                setIsOpen(false);
                              }
                            }}
                          >
                            <span>{subItem.label}</span>
                            {subItem.items && (
                              <svg
                                className="ml-2 h-4 w-4"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </button>
                          {subItem.items && hoveredSubIndex?.parent === index && hoveredSubIndex?.child === subIndex && (
                            <div className="absolute left-full top-0 -ml-1 pl-2 z-50">
                              <div className="w-auto min-w-[140px] bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600 py-1">
                                {subItem.items.map((subSubItem, subSubIndex) => (
                                  <button
                                    key={subSubIndex}
                                    className="block w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-150 whitespace-nowrap"
                                    onClick={() => {
                                      if (subSubItem.onClick) {
                                        subSubItem.onClick();
                                        setIsOpen(false);
                                      }
                                    }}
                                  >
                                    {subSubItem.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
